import React, { useState, useMemo } from 'react';

const DOC_TYPE_COLORS = {
  'News Article': '#ef4444', 'Blog Post': '#F59E0B', 'Academic Paper': '#3b82f6',
  'Wiki': '#10b981', 'Forum': '#FDE047', 'Government': '#06b6d4',
  'Video': '#FDE68A', 'PhD Informative': '#FDE047', 'PhD Dissertation': '#FCD34D',
  'Research Paper': '#6d28d9', 'Technical Drawing': '#a855f7', 'Blueprint': '#9333ea',
  'Patent': '#c026d3', 'Instructional Manual': '#d946ef', 'PDF Document': '#dc2626',
  'MS Word Document': '#2563eb', 'Informative': '#14b8a6', 'Webpage': '#64748b',
  'Personal Report (Organic)': '#10b981', 'Personal Report (Collected)': '#14b8a6',
  'Unknown': '#6b7280'
};

const MapColorLegend = ({
  results = [],
  colorMode = 'category',
  categoryColorMap = {},
  onFilterByItem,
  activeFilter
}) => {
  const [collapsed, setCollapsed] = useState(false);

  const legendItems = useMemo(() => {
    const counts = {};
    results.forEach(r => {
      if (!r.latitude || !r.longitude) return;
      let key;
      if (colorMode === 'doctype') {
        key = r.article_type || 'Unknown';
      } else {
        key = r.categories?.[0] || 'Uncategorized';
      }
      counts[key] = (counts[key] || 0) + 1;
    });

    return Object.entries(counts)
      .sort((a, b) => b[1] - a[1])
      .map(([label, count]) => {
        const color = colorMode === 'doctype'
          ? (DOC_TYPE_COLORS[label] || '#6b7280')
          : (categoryColorMap[label] || '#6b7280');
        return { label, count, color };
      });
  }, [results, colorMode, categoryColorMap]);

  if (legendItems.length === 0) return null;

  const title = colorMode === 'doctype' ? 'Document Type' : 'Category';
  const maxVisible = collapsed ? 0 : 12;
  const shown = legendItems.slice(0, maxVisible);
  const remaining = legendItems.length - maxVisible;

  return (
    <div
      data-testid="map-color-legend"
      style={{
        position: 'absolute',
        bottom: 12,
        left: 12,
        zIndex: 500,
        background: 'rgba(10, 6, 25, 0.92)',
        backdropFilter: 'blur(12px)',
        borderRadius: 10,
        border: '1px solid rgba(252, 211, 77, 0.3)',
        padding: collapsed ? '6px 10px' : '10px 12px',
        maxWidth: 220,
        maxHeight: 380,
        overflowY: 'auto',
        boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
        transition: 'all 0.3s ease',
      }}
    >
      {/* Header */}
      <div
        onClick={() => setCollapsed(prev => !prev)}
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', gap: 8, marginBottom: collapsed ? 0 : 8,
        }}
      >
        <span style={{
          color: '#047857', fontSize: '0.72rem', fontWeight: 700,
          display: 'flex', alignItems: 'center', gap: 5
        }}>
          <i className="fas fa-palette" style={{ fontSize: '0.65rem' }} />
          {title} Legend
          <span style={{ color: '#71717a', fontWeight: 500 }}>({legendItems.length})</span>
        </span>
        <i
          className={`fas fa-chevron-${collapsed ? 'up' : 'down'}`}
          style={{ color: '#71717a', fontSize: '0.6rem' }}
        />
      </div>

      {/* Legend Items */}
      {!collapsed && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          {/* Clear filter button */}
          {activeFilter && (
            <div
              data-testid="legend-clear-filter"
              onClick={() => onFilterByItem?.(null)}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '4px 6px',
                borderRadius: 5, cursor: 'pointer', marginBottom: 4,
                background: 'rgba(239, 68, 68, 0.15)', border: '1px solid rgba(239, 68, 68, 0.3)',
              }}
            >
              <i className="fas fa-times" style={{ color: '#ef4444', fontSize: '0.6rem' }} />
              <span style={{ color: '#ef4444', fontSize: '0.68rem', fontWeight: 600 }}>Clear Filter</span>
            </div>
          )}

          {shown.map(({ label, count, color }) => {
            const isActive = activeFilter === label;
            const isFiltered = activeFilter && activeFilter !== label;
            return (
              <div
                key={label}
                data-testid={`legend-item-${label.replace(/\s+/g, '-').toLowerCase()}`}
                onClick={() => onFilterByItem?.(isActive ? null : label)}
                style={{
                  display: 'flex', alignItems: 'center', gap: 7, padding: '3px 6px',
                  borderRadius: 5, cursor: 'pointer',
                  background: isActive ? `${color}20` : 'transparent',
                  opacity: isFiltered ? 0.35 : 1,
                  transition: 'all 0.2s',
                  border: isActive ? `1px solid ${color}50` : '1px solid transparent',
                }}
              >
                <div style={{
                  width: 10, height: 10, borderRadius: '50%',
                  background: color, flexShrink: 0,
                  border: '1.5px solid #334155',
                  boxShadow: `0 0 4px ${color}66`,
                }} />
                <span style={{
                  color: isActive ? '#e2e8f0' : '#a1a1aa',
                  fontSize: '0.68rem', fontWeight: isActive ? 700 : 500,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                  flex: 1,
                }} title={label}>
                  {label.length > 22 ? label.substring(0, 20) + '...' : label}
                </span>
                <span style={{
                  color: '#71717a', fontSize: '0.6rem', fontWeight: 600,
                  background: 'rgba(255,255,255,0.06)', padding: '1px 5px',
                  borderRadius: 3, flexShrink: 0,
                }}>
                  {count}
                </span>
              </div>
            );
          })}

          {remaining > 0 && (
            <div style={{
              color: '#71717a', fontSize: '0.62rem', textAlign: 'center',
              padding: '3px 0', borderTop: '1px solid rgba(255,255,255,0.06)', marginTop: 2,
            }}>
              +{remaining} more
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default MapColorLegend;
